import {
  Calendar,
  Check as CheckIcon,
  CircleDollarSign,
  Pencil as PencilIcon,
} from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import PlusIcon from '@/assets/icons/plus-sm.svg';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tag } from '@/components/ui/tag';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { useIsOverflowing } from '@/hooks/use-is-overflowing';
import { cn } from '@/utils/classnames';

import { ChannelInfoSheet } from '../channel-info-sheet';
import { OpeningSelectionSheet } from '../opening-selection-sheet';

interface CardLayoutProps {
  /**
   * Unique form-state key for this channel. For direct channels this equals the channel
   * slug; for Vonq channels it's the product ID. Used only for React-Hook-Form paths.
   */
  channelKey: string;
  channelSlug: string;
  icon: React.ReactNode;
  displayTitle: string;
  /** Fixed duration in days (takes precedence over minDuration). */
  recommendedDuration?: number;
  /** Minimum duration in days — shown as "Min X days" when no fixed duration is set. */
  minDuration?: number;
  /** Fixed recommended budget per opening (takes precedence over minBudget). */
  recommendedBudget?: number;
  /** Minimum budget per opening — shown as "Min $X" when no fixed budget is set. */
  minBudget?: number;
  channelType: string;
  description: React.ReactNode | null;
  processingTime?: string;
  locations?: string[];
  industries?: string[];
  /**
   * Raw description text and a larger icon for the "More info" sheet.
   */
  infoSheet: {
    icon: React.ReactNode;
    description: string | null;
  };
  disabled?: boolean;
  onSelect: () => void;
  isFree?: boolean;
  openingsSelectedForAnotherProduct?: Set<string>;
}

export function CardLayout({
  channelKey,
  channelSlug,
  icon,
  displayTitle,
  recommendedDuration,
  minDuration,
  recommendedBudget,
  minBudget,
  channelType,
  description,
  processingTime,
  locations,
  industries,
  infoSheet,
  disabled = false,
  onSelect,
  isFree = false,
  openingsSelectedForAnotherProduct = new Set(),
}: CardLayoutProps) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const isOverflowing = useIsOverflowing(descriptionRef, true);
  const { hookForm, selectedOpeningIds } = useJobPostingContext();
  const [isInfoSheetOpen, setIsInfoSheetOpen] = useState(false);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);
  const titleRef = useCallback(
    (el: HTMLSpanElement | null) => {
      if (!el) return;
      setIsTitleTruncated(el.scrollWidth > el.clientWidth);
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [displayTitle],
  );

  const totalOpeningsCount = selectedOpeningIds.length;
  const openingBudgets =
    useWatch({
      control: hookForm.control,
      name: `channels.${channelKey}.openingBudgets` as const,
    }) ?? {};
  const selectedOpeningsCount = Object.keys(openingBudgets).length;
  const isSelected = selectedOpeningsCount > 0;
  const isAllSelected = selectedOpeningsCount === totalOpeningsCount;

  const [isOpeningSelectionSheetOpen, setIsOpeningSelectionSheetOpen] =
    useState(false);

  /* Budget to display on the card and in the info sheet:
     - Free channels → "Free"
     - Fixed budget → formatted currency value
     - Minimum budget → "Min $X"
     - Otherwise → "Custom" */
  const displayBudget = useMemo(() => {
    if (isFree) {
      return intl.formatMessage({
        defaultMessage: 'Free',
        description: 'Free budget label on the channel card',
      });
    }

    if (recommendedBudget) {
      return formatCurrency(recommendedBudget);
    }

    if (minBudget != null) {
      return intl.formatMessage(
        {
          defaultMessage: 'Min {amount}',
          description:
            'Minimum budget label on the channel card, e.g. "Min $300"',
        },
        { amount: formatCurrency(minBudget) },
      );
    }

    return intl.formatMessage({
      defaultMessage: 'Custom',
      description: 'Custom budget label on the channel card',
    });
  }, [formatCurrency, intl, isFree, minBudget, recommendedBudget]);

  /* Duration to display on the card and in the info sheet:
     - Fixed duration → "X days"
     - Minimum duration → "Min X days"
     - Otherwise → null (card tag falls back to "Set-up in next step"; info sheet hides the field) */
  const displayDuration = useMemo(() => {
    if (recommendedDuration != null) {
      return intl.formatMessage(
        {
          defaultMessage: '{duration} days',
          description:
            'Fixed duration label on the channel card, e.g. "30 days"',
        },
        { duration: recommendedDuration },
      );
    }

    if (minDuration != null) {
      return intl.formatMessage(
        {
          defaultMessage: 'Min {duration} days',
          description:
            'Minimum duration label on the channel card, e.g. "Min 7 days"',
        },
        { duration: minDuration },
      );
    }

    return intl.formatMessage({
      defaultMessage: 'Set-up in next step',
      description:
        'Duration label when the duration is configured in the next step',
    });
  }, [recommendedDuration, intl, minDuration]);

  /* Handle the click on the add channel button - selects all openings and opens the opening selection sheet */
  const handleAddClick = () => {
    onSelect();
    setIsOpeningSelectionSheetOpen(true);
  };

  return (
    <>
      <div className="flex flex-col">
        <div
          className={cn(
            'border-fx-neutral-200 z-10 flex items-start justify-between gap-4 rounded-lg border bg-white px-4 py-3',
            disabled && 'bg-fx-neutral-50',
            isSelected && '-mb-2',
          )}
        >
          <div className="flex w-full min-w-0 flex-col gap-2">
            <div className="flex w-full items-center gap-2 py-1">
              {/* Channel icon */}
              {icon}

              <div className="flex min-w-0 flex-col gap-1">
                {/* Product title / Channel name */}
                <Tooltip content={isTitleTruncated ? displayTitle : undefined}>
                  <Typography
                    ref={titleRef}
                    variant="body2"
                    className="truncate"
                  >
                    {displayTitle}
                  </Typography>
                </Tooltip>
                <div className="flex items-center gap-2">
                  {/* Recommended budget */}
                  <Tag
                    icon={
                      <CircleDollarSign className="text-fx-neutral-600 size-3" />
                    }
                    text={displayBudget}
                    className="bg-fx-neutral-100"
                    textClassName="text-fx-neutral-900"
                  />
                  {/* Duration */}
                  <Tag
                    icon={<Calendar className="text-fx-neutral-600 size-3" />}
                    text={displayDuration}
                    className="bg-fx-neutral-100"
                    textClassName="text-fx-neutral-900"
                  />
                  {/* Channel type */}
                  <Tag
                    text={channelType}
                    className="bg-fx-brand-50"
                    textClassName="text-fx-brand-800"
                  />
                </div>
              </div>
            </div>

            {/* Channel description and more info button */}
            {description && (
              <div className="flex w-full min-w-0 items-baseline gap-1">
                <Typography
                  ref={descriptionRef}
                  variant="body1"
                  className="text-fx-neutral-600 min-w-0 flex-1 truncate"
                >
                  {description}
                </Typography>
                {isOverflowing && infoSheet && (
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => setIsInfoSheetOpen(true)}
                    className="decoration-fx-brand-500 h-fit shrink-0 underline !underline-offset-1"
                  >
                    <Typography variant="small1" className="text-fx-brand-500">
                      <FormattedMessage
                        defaultMessage="More info"
                        description="Inline link shown at the end of a description that is overflowing"
                      />
                    </Typography>
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Add channel button - selects all openings and opens the opening selection sheet */}
          <Button
            variant={isSelected ? 'brand' : 'secondary'}
            type="button"
            disabled={disabled}
            onClick={handleAddClick}
            className="w-[96px]"
          >
            {isSelected ? (
              <>
                <CheckIcon className="size-4 text-white" />
                <FormattedMessage
                  defaultMessage="Added"
                  description="Button to indicate that the channel has been added to the job posting"
                />
              </>
            ) : (
              <>
                <img src={PlusIcon} alt="" />
                <FormattedMessage
                  defaultMessage="Add"
                  description="Button to add the channel to the job posting"
                />
              </>
            )}
          </Button>
        </div>

        {/* Selection indicator with edit button */}
        {isSelected && (
          <div className="bg-fx-brand-50 border-fx-neutral-200 flex items-center gap-2 rounded-b-lg border px-4 pt-2 pb-1">
            <Typography variant="body1" className="text-fx-neutral-600">
              <FormattedMessage
                defaultMessage="Added:"
                description="Caption for the number of openings added to the channel"
              />
            </Typography>
            <Typography
              variant="body1"
              className="text-fx-neutral-600 font-bold"
            >
              {isAllSelected ? (
                <FormattedMessage
                  defaultMessage="All {count} openings"
                  description="Caption for the number of openings added to the channel"
                  values={{ count: totalOpeningsCount }}
                />
              ) : (
                <FormattedMessage
                  defaultMessage="{count} of {total} openings"
                  description="Caption for the number of openings added to the channel"
                  values={{
                    count: selectedOpeningsCount,
                    total: totalOpeningsCount,
                  }}
                />
              )}
            </Typography>
            <Button
              variant="icon"
              onClick={handleAddClick}
              type="button"
              className="h-8 w-8"
            >
              <PencilIcon className="text-fx-brand-600 size-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Opening selection sheet */}
      <OpeningSelectionSheet
        open={isOpeningSelectionSheetOpen}
        onOpenChange={setIsOpeningSelectionSheetOpen}
        channelIcon={icon}
        channelKey={channelKey}
        channelSlug={channelSlug}
        displayTitle={displayTitle}
        recommendedBudget={recommendedBudget}
        recommendedDuration={recommendedDuration}
        isFree={isFree}
        openingsSelectedForAnotherProduct={openingsSelectedForAnotherProduct}
      />

      {/* Channel info sheet */}
      {infoSheet.description && (
        <ChannelInfoSheet
          open={isInfoSheetOpen}
          onOpenChange={setIsInfoSheetOpen}
          title={displayTitle}
          icon={infoSheet.icon}
          description={infoSheet.description}
          displayDuration={displayDuration}
          displayBudget={displayBudget}
          channelType={channelType}
          processingTime={processingTime}
          locations={locations}
          industries={industries}
        />
      )}
    </>
  );
}

CardLayout.Skeleton = function CardLayoutSkeleton() {
  return (
    <div className="flex flex-col">
      <div className="border-fx-neutral-200 z-10 flex items-start justify-between gap-4 rounded-lg border bg-white px-4 py-3">
        <div className="flex w-full min-w-0 flex-col gap-2">
          <div className="flex w-full items-center gap-2 py-1">
            <Skeleton className="h-6 w-24" />

            <div className="flex flex-col gap-1">
              <Skeleton className="mb-1 h-4 w-60" />
              <div className="flex items-center gap-2">
                <Skeleton className="h-6 w-16 rounded-full" />
                <Skeleton className="h-6 w-20 rounded-full" />
                <Skeleton className="h-6 w-24 rounded-full" />
              </div>
            </div>
          </div>
          <Skeleton className="h-5 w-full" />
        </div>
        <Skeleton className="mt-1 h-8 w-22" />
      </div>
    </div>
  );
};
